import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import {
  Search, ChevronDown, ChevronRight, FileText, Calculator, Eye, BookOpen,
  CheckCircle2, AlertTriangle, HelpCircle, Loader2, ClipboardList, Layers
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_CONFIG = {
  measured_from_drawing: { label: 'Measured', icon: Eye, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  visual_measurement: { label: 'Measured', icon: Eye, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  spec_listed: { label: 'From Spec', icon: BookOpen, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  specification_reference: { label: 'From Spec', icon: BookOpen, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  schedule_reference: { label: 'Schedule', icon: ClipboardList, color: 'bg-teal-50 text-teal-700 border-teal-200' },
  counted_from_plan: { label: 'Counted', icon: ClipboardList, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  direct_count: { label: 'Counted', icon: ClipboardList, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  calculated: { label: 'Calculated', icon: Calculator, color: 'bg-[#72A4BF]/10 text-[#0D1830] border-[#1D4053]/20' },
  estimated: { label: 'Estimated', icon: HelpCircle, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  assembly_resolution: { label: 'Assembly', icon: Layers, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  unknown: { label: 'Unknown', icon: HelpCircle, color: 'bg-gray-50 text-gray-500 border-gray-200' },
};

function ConfidencePill({ value }) {
  const pct = ((value || 0) * 100).toFixed(0);
  const color = value >= 0.8 ? 'text-emerald-700 bg-emerald-50' : value >= 0.5 ? 'text-[#0D1830] bg-[#72A4BF]/10' : 'text-red-600 bg-red-50';
  const Icon = value >= 0.8 ? CheckCircle2 : value >= 0.5 ? AlertTriangle : HelpCircle;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${color}`}>
      <Icon className="w-2.5 h-2.5" />{pct}%
    </span>
  );
}

function AuditRow({ comp, onViewDrawing }) {
  const [open, setOpen] = useState(false);
  const method = METHOD_CONFIG[comp.calculation_method] || METHOD_CONFIG.unknown;
  const MethodIcon = method.icon;
  const fmtC = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <>
      <TableRow
        className={`border-[#F0F4F7] cursor-pointer hover:bg-[#F7FAFB] transition-colors ${open ? 'bg-[#F7FAFB]' : ''}`}
        onClick={() => setOpen(!open)}
        data-testid={`audit-row-${comp.component_name}`}
      >
        <TableCell className="text-xs">
          <div className="flex items-center gap-1.5">
            {open ? <ChevronDown className="w-3 h-3 text-[#72A4BF] shrink-0" /> : <ChevronRight className="w-3 h-3 text-[#72A4BF] shrink-0" />}
            <span className="font-medium">{comp.component_name}</span>
          </div>
        </TableCell>
        <TableCell className="text-xs text-right">{comp.quantity} {comp.unit}</TableCell>
        <TableCell className="text-xs text-right">{fmtC(comp.total_cost)}</TableCell>
        <TableCell className="text-xs">
          <Badge className={`text-[9px] px-1.5 py-0 rounded-full border ${method.color}`}>
            <MethodIcon className="w-2.5 h-2.5 mr-0.5" />{method.label}
          </Badge>
        </TableCell>
        <TableCell className="text-xs"><ConfidencePill value={comp.confidence} /></TableCell>
        <TableCell className="text-xs text-[#72A4BF] max-w-[120px] truncate">{comp.page_reference || '-'}</TableCell>
        <TableCell className="text-xs">
          {comp.source_assembly ? (
            <Badge className="text-[9px] px-1.5 py-0 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200">
              <Layers className="w-2.5 h-2.5 mr-0.5" />{comp.source_assembly}
            </Badge>
          ) : (
            <span className="text-[#72A4BF]">—</span>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="border-[#F0F4F7]" data-testid={`audit-detail-${comp.component_name}`}>
          <TableCell colSpan={7} className="p-0">
            <div className="px-6 py-4 bg-[#F7FAFB] border-t border-dashed border-[#E2E8ED] space-y-3">
              {/* Source info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-1">Division / Assembly</p>
                  <p className="text-xs text-[#0D1830]">{comp.division}</p>
                  <p className="text-[10px] text-[#446E87]">{comp.assembly}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-1">Source</p>
                  <div className="flex items-center gap-1.5">
                    <FileText className="w-3 h-3 text-[#0D1830]" />
                    <span className="text-xs text-[#0D1830]">{comp.source_file || 'N/A'}</span>
                  </div>
                  <p className="text-[10px] text-[#446E87] mt-0.5">{comp.page_reference || 'No page ref'}</p>
                  {comp.page_reference && onViewDrawing && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onViewDrawing(comp.source_file, comp.page_reference); }}
                      className="mt-1 text-[10px] text-[#0D1830] hover:underline flex items-center gap-0.5"
                      data-testid={`view-drawing-${comp.component_name}`}
                    >
                      <Eye className="w-2.5 h-2.5" />View on Drawing
                    </button>
                  )}
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-1">Cost Breakdown</p>
                  <p className="text-xs text-[#0D1830]">{comp.quantity} {comp.unit} x {fmtC(comp.unit_cost)} = <span className="font-semibold gold-text">{fmtC(comp.total_cost)}</span></p>
                </div>
              </div>

              {/* Source text */}
              {comp.source_text && (
                <div>
                  <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-1">Source Text from Document</p>
                  <div className="bg-white border border-[#E2E8ED] rounded-lg p-3">
                    <p className="text-xs text-[#444] italic leading-relaxed">"{comp.source_text}"</p>
                  </div>
                </div>
              )}

              {/* Calculation steps */}
              {comp.calculation_steps && comp.calculation_steps.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-1">Calculation Steps</p>
                  <div className="bg-white border border-[#E2E8ED] rounded-lg p-3 space-y-1.5">
                    {comp.calculation_steps.map((step, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="w-4 h-4 rounded-full bg-[#0D1830] text-white text-[9px] flex items-center justify-center shrink-0 mt-0.5 font-bold">{i + 1}</span>
                        <p className="text-xs text-[#444]">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {comp.notes && (
                <div>
                  <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-1">Notes</p>
                  <p className="text-xs text-[#446E87]">{comp.notes}</p>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function AuditTrailView({ projectId, onViewDrawing }) {
  const [data, setData] = useState(null);
  const [assemblyAudit, setAssemblyAudit] = useState([]);
  const [unresolvedAssemblies, setUnresolvedAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [confidenceFilter, setConfidenceFilter] = useState('all');

  const fetchAuditTrail = useCallback(async () => {
    try {
      const [auditRes, takeoffRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}/takeoff/audit-trail`, { credentials: 'include' }),
        fetch(`${API}/projects/${projectId}/takeoff`, { credentials: 'include' }),
      ]);
      if (auditRes.ok) setData(await auditRes.json());
      if (takeoffRes.ok) {
        const td = await takeoffRes.json();
        setAssemblyAudit(td.assembly_audit || []);
        setUnresolvedAssemblies(td.unresolved_assemblies || []);
      }
    } catch (e) {}
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchAuditTrail(); }, [fetchAuditTrail]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>;
  if (!data || data.components.length === 0) {
    return (
      <Card className="border-dashed border-2 border-[#E2E8ED] shadow-none">
        <CardContent className="py-16 text-center">
          <Calculator className="w-10 h-10 text-[#1D4053]/30 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-[#0D1830] mb-1">Calculation Audit Trail</h3>
          <p className="text-sm text-[#72A4BF] max-w-md mx-auto">
            Run AI analysis on your documents to generate the audit trail. Each component will show how its quantity was derived.
          </p>
        </CardContent>
      </Card>
    );
  }

  const summary = data.summary;
  const filtered = data.components.filter(c => {
    if (search && !c.component_name.toLowerCase().includes(search.toLowerCase()) && !c.division.toLowerCase().includes(search.toLowerCase())) return false;
    if (methodFilter !== 'all' && c.calculation_method !== methodFilter) return false;
    if (confidenceFilter === 'high' && c.confidence < 0.8) return false;
    if (confidenceFilter === 'medium' && (c.confidence < 0.5 || c.confidence >= 0.8)) return false;
    if (confidenceFilter === 'low' && c.confidence >= 0.5) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="audit-trail-view">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center"><Calculator className="w-4 h-4 text-[#0D1830]" /></div>
            <div><p className="text-lg font-bold text-[#0D1830]">{summary.total_components}</p><p className="text-xs text-[#72A4BF]">Total Components</p></div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100 shadow-none bg-emerald-50/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></div>
            <div><p className="text-lg font-bold text-emerald-700">{summary.confidence_breakdown.high}</p><p className="text-xs text-[#72A4BF]">High Confidence</p></div>
          </CardContent>
        </Card>
        <Card className="border-[#1D4053]/20 shadow-none bg-[#72A4BF]/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-[#0D1830]" /></div>
            <div><p className="text-lg font-bold text-[#0D1830]">{summary.confidence_breakdown.medium}</p><p className="text-xs text-[#72A4BF]">Medium Confidence</p></div>
          </CardContent>
        </Card>
        <Card className="border-red-100 shadow-none bg-red-50/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center"><HelpCircle className="w-4 h-4 text-red-500" /></div>
            <div><p className="text-lg font-bold text-red-600">{summary.confidence_breakdown.low}</p><p className="text-xs text-[#72A4BF]">Low Confidence</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Method breakdown */}
      <Card className="border-[#E2E8ED] shadow-none">
        <CardContent className="p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-[#72A4BF] font-medium">Methods:</span>
            {Object.entries(summary.method_breakdown).map(([method, count]) => {
              const mc = METHOD_CONFIG[method] || METHOD_CONFIG.unknown;
              const MIcon = mc.icon;
              return (
                <Badge key={method} className={`text-[10px] px-2 py-0.5 rounded-full border cursor-pointer transition-opacity ${mc.color} ${methodFilter === method ? 'ring-2 ring-[#1D4053] ring-offset-1' : 'opacity-80 hover:opacity-100'}`}
                  onClick={() => setMethodFilter(methodFilter === method ? 'all' : method)}
                  data-testid={`method-filter-${method}`}>
                  <MIcon className="w-2.5 h-2.5 mr-0.5" />{mc.label}: {count}
                </Badge>
              );
            })}
            {methodFilter !== 'all' && (
              <button onClick={() => setMethodFilter('all')} className="text-[10px] text-[#0D1830] hover:underline">Clear</button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assembly Resolution Audit */}
      {assemblyAudit.length > 0 && (
        <Card className="border-indigo-200 shadow-none bg-indigo-50/20" data-testid="assembly-audit-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Layers className="w-4 h-4 text-indigo-600" />
              Assembly Resolution ({assemblyAudit.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {assemblyAudit.map((asm, i) => (
              <div key={i} className="bg-white border border-indigo-100 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border-0 font-bold">{asm.assembly_code}</Badge>
                    <span className="text-xs text-[#0D1830] font-medium">{asm.assembly_type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#72A4BF]">{asm.total_quantity} {asm.total_unit}</span>
                    <ConfidencePill value={asm.confidence} />
                    <Badge className={`text-[9px] px-1.5 py-0 rounded-full border-0 ${asm.resolution_status === 'fully_resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                      {asm.resolution_status === 'fully_resolved' ? 'Resolved' : 'Partial'}
                    </Badge>
                  </div>
                </div>
                {asm.instances_found?.length > 0 && (
                  <div className="text-[10px] text-[#72A4BF] mb-2">
                    Found: {asm.instances_found.map((inst, j) => (
                      <span key={j} className="inline-flex items-center gap-1 mr-3">{inst.location} — {inst.quantity} {inst.unit} <span className="text-[#446E87]">({inst.source_page})</span></span>
                    ))}
                  </div>
                )}
                {asm.components_derived?.length > 0 && (
                  <div className="border-t border-indigo-100 pt-2 space-y-1">
                    <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">Components Derived</p>
                    {asm.components_derived.map((cd, j) => (
                      <div key={j} className="flex items-center justify-between text-xs">
                        <span className="text-[#0D1830]">{cd.component_name}</span>
                        <span className="text-[#72A4BF] font-mono text-[10px]">{cd.calculation || `${cd.quantity} ${cd.unit}`}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Unresolved Assemblies */}
      {unresolvedAssemblies.length > 0 && (
        <Card className="border-orange-200 shadow-none bg-orange-50/20" data-testid="unresolved-assemblies-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Unresolved Assemblies ({unresolvedAssemblies.length})
              <span className="text-[10px] font-normal text-orange-500">Needs manual review</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {unresolvedAssemblies.map((u, i) => (
              <div key={i} className="bg-white border border-orange-200 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <Badge className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border-0 font-bold">{u.assembly_code}</Badge>
                  <span className="text-xs text-orange-600 ml-2">{u.reason}</span>
                  {u.instances_found?.length > 0 && (
                    <p className="text-[10px] text-[#72A4BF] mt-1">
                      Found at: {u.instances_found.map(inst => `${inst.location} (${inst.source_page})`).join(', ')}
                    </p>
                  )}
                </div>
                <Badge className="text-[9px] px-1.5 py-0 rounded-full bg-orange-100 text-orange-700 border-0">
                  {u.temporary_line_item ? 'Temp Line Item' : 'Flagged'}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-[#E2E8ED] shadow-none">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#72A4BF]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search components or divisions..."
              className="text-xs h-8 pl-8"
              data-testid="audit-search"
            />
          </div>
          <select
            value={confidenceFilter}
            onChange={(e) => setConfidenceFilter(e.target.value)}
            className="text-xs border border-[#E2E8ED] rounded-lg px-2 py-1.5 bg-white h-8"
            data-testid="confidence-filter"
          >
            <option value="all">All Confidence</option>
            <option value="high">High (80%+)</option>
            <option value="medium">Medium (50-80%)</option>
            <option value="low">Low (&lt;50%)</option>
          </select>
          <span className="text-xs text-[#72A4BF]">{filtered.length} of {data.components.length} components</span>
        </CardContent>
      </Card>

      {/* Audit table */}
      <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
              <TableHead className="text-xs">Component</TableHead>
              <TableHead className="text-xs text-right">Quantity</TableHead>
              <TableHead className="text-xs text-right">Total Cost</TableHead>
              <TableHead className="text-xs">Method</TableHead>
              <TableHead className="text-xs">Confidence</TableHead>
              <TableHead className="text-xs">Source</TableHead>
              <TableHead className="text-xs">Assembly</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((comp, i) => (
              <AuditRow key={i} comp={comp} onViewDrawing={onViewDrawing} />
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && (
          <div className="py-8 text-center text-xs text-[#72A4BF]">No components match your filters.</div>
        )}
      </Card>
    </div>
  );
}
